import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  cloneGiftFundItem,
  getActiveGiftFundItems,
  getArchivedGiftFundItems,
} from '../../../api/marketing/gifts/giftFundHelpers'
import {
  GIFT_FUND_REMAINS_STATUS,
  buildCatalogProductLookup,
} from '../../../api/marketing/gifts/giftFundCatalogPool'
import { loadGiftFundCatalogAvailability } from '../../../api/marketing/gifts/loadGiftFundCatalogAvailability'
import getGiftFundItems from '../../../api/marketing/gifts/getGiftFundItems'
import saveGiftFundItemApi from '../../../api/marketing/gifts/saveGiftFundItem'
import setGiftFundItemArchivedApi from '../../../api/marketing/gifts/setGiftFundItemArchived'

const GiftsFundContext = createContext(null)

export function GiftsFundProvider({ children }) {
  const [items, setItems] = useState([])
  const [loadStatus, setLoadStatus] = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [loadNonce, setLoadNonce] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [catalogAvailability, setCatalogAvailability] = useState({
    remainsStatus: GIFT_FUND_REMAINS_STATUS.idle,
    remainsError: '',
    products: [],
    outletProducts: [],
    taxonomyNodes: [],
    remainsByShop: null,
    productsByKey: {},
  })

  const reloadItems = useCallback(() => {
    setLoadNonce((value) => value + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoadStatus('loading')
      setLoadError(null)

      try {
        const nextItems = await getGiftFundItems()

        if (cancelled) {
          return
        }

        setItems(nextItems)
        setLoadStatus('ready')
      } catch (error) {
        console.error('[GiftsFundProvider] load failed:', error)

        if (cancelled) {
          return
        }

        setItems([])
        setLoadStatus('error')
        setLoadError(error?.message || 'Не вдалося завантажити подарунковий фонд')
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [loadNonce])

  useEffect(() => {
    let cancelled = false

    setCatalogAvailability((prev) => ({
      ...prev,
      remainsStatus: GIFT_FUND_REMAINS_STATUS.loading,
      remainsError: '',
    }))

    loadGiftFundCatalogAvailability()
      .then((data) => {
        if (cancelled) {
          return
        }

        setCatalogAvailability({
          remainsStatus: GIFT_FUND_REMAINS_STATUS.ready,
          remainsError: '',
          products: data.products,
          outletProducts: data.outletProducts,
          taxonomyNodes: data.taxonomyNodes,
          remainsByShop: data.remainsByShop,
          productsByKey: buildCatalogProductLookup(data.products, data.outletProducts),
        })
      })
      .catch((error) => {
        console.error('[GiftsFundProvider] catalog availability failed:', error)
        if (cancelled) {
          return
        }
        setCatalogAvailability((prev) => ({
          ...prev,
          remainsStatus: GIFT_FUND_REMAINS_STATUS.error,
          remainsError: 'Не вдалося завантажити наявність каталогу',
          remainsByShop: null,
        }))
      })

    return () => {
      cancelled = true
    }
  }, [])

  const saveItem = useCallback(async (draft) => {
    setIsSaving(true)

    try {
      const result = await saveGiftFundItemApi(draft)

      if (!result.ok) {
        return result
      }

      const saved = cloneGiftFundItem(result.item)
      setItems((prev) => {
        const index = prev.findIndex((row) => row.id === saved.id)
        if (index < 0) {
          return [...prev, saved]
        }
        const next = [...prev]
        next[index] = saved
        return next
      })

      return result
    } finally {
      setIsSaving(false)
    }
  }, [])

  const archiveItem = useCallback(async (itemId) => {
    setIsSaving(true)

    try {
      const result = await setGiftFundItemArchivedApi({ itemId, isArchived: true })

      if (!result.ok) {
        return result
      }

      const saved = cloneGiftFundItem(result.item)
      setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)))
      return result
    } finally {
      setIsSaving(false)
    }
  }, [])

  const restoreItem = useCallback(async (itemId) => {
    setIsSaving(true)

    try {
      const result = await setGiftFundItemArchivedApi({ itemId, isArchived: false })

      if (!result.ok) {
        return result
      }

      const saved = cloneGiftFundItem(result.item)
      setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)))
      return result
    } finally {
      setIsSaving(false)
    }
  }, [])

  const getItemById = useCallback((itemId) => items.find((item) => item.id === itemId) || null, [items])

  const value = useMemo(
    () => ({
      items,
      activeItems: getActiveGiftFundItems(items),
      archivedItems: getArchivedGiftFundItems(items),
      loadStatus,
      loadError,
      isSaving,
      reloadItems,
      saveItem,
      archiveItem,
      restoreItem,
      getItemById,
      catalogAvailability,
    }),
    [
      items,
      loadStatus,
      loadError,
      isSaving,
      reloadItems,
      saveItem,
      archiveItem,
      restoreItem,
      getItemById,
      catalogAvailability,
    ]
  )

  return <GiftsFundContext.Provider value={value}>{children}</GiftsFundContext.Provider>
}

export function useGiftsFund() {
  const value = useContext(GiftsFundContext)

  if (!value) {
    throw new Error('useGiftsFund must be used within GiftsFundProvider')
  }

  return value
}
